import { clsx } from 'clsx';

interface TimelineStep {
  id: string;
  label: string;
  description?: string;
  status: 'completed' | 'current' | 'upcoming';
  date?: string;
}

interface ClaimTimelineProps {
  claimStatus: string;
  createdAt: string;
  submittedAt?: string | null;
  councilResponseAt?: string | null;
  outcome?: string | null;
}

const STATUS_ORDER = [
  'draft',
  'evidence_complete',
  'pack_generated',
  'submitted',
  'awaiting_response',
  'response_received',
  'resolved',
];

export function ClaimTimeline({
  claimStatus,
  createdAt,
  submittedAt,
  councilResponseAt,
  outcome,
}: ClaimTimelineProps) {
  const currentIndex = STATUS_ORDER.indexOf(claimStatus);

  const getStepStatus = (stepIndex: number): 'completed' | 'current' | 'upcoming' => {
    if (stepIndex < currentIndex) return 'completed';
    if (stepIndex === currentIndex) return 'current';
    return 'upcoming';
  };

  const steps: TimelineStep[] = [
    {
      id: 'draft',
      label: 'Claim Created',
      description: 'Started gathering evidence',
      status: getStepStatus(0),
      date: createdAt,
    },
    {
      id: 'evidence_complete',
      label: 'Evidence Complete',
      description: 'All required documents uploaded',
      status: getStepStatus(1),
    },
    {
      id: 'pack_generated',
      label: 'Pack Generated',
      description: 'Claim pack ready for download',
      status: getStepStatus(2),
    },
    {
      id: 'submitted',
      label: 'Submitted to Council',
      description: 'Claim sent to highway authority',
      status: getStepStatus(3),
      date: submittedAt || undefined,
    },
    {
      id: 'response_received',
      label: 'Council Response',
      description: outcome ? `Outcome: ${outcome}` : 'Awaiting response',
      status: getStepStatus(5),
      date: councilResponseAt || undefined,
    },
  ];

  return (
    <div className="relative">
      <div className="space-y-0">
        {steps.map((step, index) => (
          <div key={step.id} className="relative flex gap-4">
            {/* Line */}
            {index < steps.length - 1 && (
              <div
                className={clsx(
                  'absolute left-[15px] top-8 w-0.5 h-full -ml-px',
                  step.status === 'completed' ? 'bg-success-500' : 'bg-surface-200'
                )}
              />
            )}

            {/* Icon */}
            <div
              className={clsx(
                'relative z-10 flex h-8 w-8 items-center justify-center rounded-full border-2',
                step.status === 'completed' && 'bg-success-500 border-success-500',
                step.status === 'current' && 'bg-brand-600 border-brand-600',
                step.status === 'upcoming' && 'bg-white border-surface-300'
              )}
            >
              {step.status === 'completed' ? (
                <svg className="w-4 h-4 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" />
                </svg>
              ) : step.status === 'current' ? (
                <div className="w-2 h-2 rounded-full bg-white" />
              ) : (
                <div className="w-2 h-2 rounded-full bg-surface-300" />
              )}
            </div>

            {/* Content */}
            <div className="pb-8 pt-1 flex-1">
              <p
                className={clsx(
                  'font-medium text-sm',
                  step.status === 'upcoming' ? 'text-surface-400' : 'text-brand-950'
                )}
              >
                {step.label}
              </p>
              {step.description && (
                <p
                  className={clsx(
                    'text-xs mt-0.5',
                    step.status === 'upcoming' ? 'text-surface-300' : 'text-surface-600'
                  )}
                >
                  {step.description}
                </p>
              )}
              {step.date && step.status !== 'upcoming' && (
                <p className="text-xs text-surface-500 mt-1">
                  {new Date(step.date).toLocaleDateString('en-GB', {
                    day: 'numeric',
                    month: 'short',
                    year: 'numeric',
                  })}
                </p>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}
